import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setSelectedRobotId } from 'store/robots/robots-slice';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';

import {
  EnvironmentPollution,
  EnvironmentPollutionType,
} from '@common/interfaces/robot/cleaning-configurations/environment-pollution';
import { getInitialLocalRobotConfigurationState } from './local-robot-configuration-slice-definitions';
import {
  deleteExcludedDateById,
  getEnvironmentPollution,
  getExcludedDates,
  getNavLocalPlanner,
  getNavTasks,
  getParamList,
  patchCustomEnvironmentPollution,
  patchExcludedDateById,
  patchNavLocalPlanner,
  patchNavTasks,
  patchParamList,
  postExcludedDate,
  getButtonConfiguration,
} from './local-robot-configuration-actions';
import RestSet, { OneToOneRestSet } from '@common/interfaces/api/rest-set';
import { updateDataList } from 'utils/generic-data-list-handlers';

const initialLocalRobotConfigurationState = getInitialLocalRobotConfigurationState();

const localRobotConfigurationSlice = createSlice({
  name: 'local-robot-configuration',
  initialState: initialLocalRobotConfigurationState,
  reducers: {
    resetLocalRobotConfigurationState(state) {
      return {
        ...initialLocalRobotConfigurationState,
      };
    },
    updateCustomEnvironmentPollutionConfiguration(
      state,
      action: PayloadAction<Partial<EnvironmentPollution>>,
    ) {
      const customConfigIndex = state.cleaningConfiguration.environmentPollution.data?.findIndex(
        (c) => c.type === EnvironmentPollutionType.Custom,
      );
      if (customConfigIndex != null && customConfigIndex !== -1) {
        const customConfig =
          state.cleaningConfiguration.environmentPollution.data![customConfigIndex];
        state.cleaningConfiguration.environmentPollution.data![customConfigIndex] = {
          ...customConfig,
          ...action.payload,
        };
      }
    },
    patchNavLocalPlannerRestSet(state, action: PayloadAction<Partial<OneToOneRestSet>>) {
      state.nav.localPlanner.rest = { ...state.nav.localPlanner.rest, ...action.payload };
    },
    patchNavTasksRestSet(state, action: PayloadAction<Partial<OneToOneRestSet>>) {
      state.nav.tasks.rest = { ...state.nav.tasks.rest, ...action.payload };
    },
    patchParamListRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.paramList.rest = { ...state.paramList.rest, ...action.payload };
    },
    patchExcludedDatesRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.excludedDates.rest = { ...state.excludedDates.rest, ...action.payload };
    },
    patchEnvironmentPollutionRestSet(state, action: PayloadAction<Partial<RestSet>>) {
      state.cleaningConfiguration.environmentPollution.rest = {
        ...state.cleaningConfiguration.environmentPollution.rest,
        ...action.payload,
      };
    },
    toggleWaypointsEnabledAutomatically(state) {
      state.nav.tasks.waypointsEnabledAutomatically =
        !state.nav.tasks.waypointsEnabledAutomatically;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setSelectedRobotId, (state) => {
      // Reset state when id changes
      return {
        ...initialLocalRobotConfigurationState,
      };
    });
    builder.addCase(getParamList.pending, (state, action) => {
      state.paramList.rest.getStatus = HttpFetchStatus.Loading;
      state.paramList.rest.getError = null;
    });
    builder.addCase(getParamList.fulfilled, (state, action) => {
      state.paramList.data = action.payload;
      state.paramList.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getParamList.rejected, (state, action) => {
      state.paramList.rest.getStatus = HttpFetchStatus.Failed;
      state.paramList.rest.getError = action.payload ?? null;
    });
    builder.addCase(patchParamList.pending, (state, action) => {
      state.paramList.rest.patchStatus = HttpFetchStatus.Loading;
      state.paramList.rest.patchError = null;
    });
    builder.addCase(patchParamList.fulfilled, (state, action) => {
      state.paramList.data = action.payload;
      state.paramList.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchParamList.rejected, (state, action) => {
      state.paramList.rest.patchStatus = HttpFetchStatus.Failed;
      state.paramList.rest.patchError = action.payload ?? null;
    });

    builder.addCase(getEnvironmentPollution.pending, (state, action) => {
      state.cleaningConfiguration.environmentPollution.rest.getStatus = HttpFetchStatus.Loading;
      state.cleaningConfiguration.environmentPollution.rest.getError = null;
    });
    builder.addCase(getEnvironmentPollution.fulfilled, (state, action) => {
      state.cleaningConfiguration.environmentPollution.data = action.payload;
      state.cleaningConfiguration.environmentPollution.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getEnvironmentPollution.rejected, (state, action) => {
      state.cleaningConfiguration.environmentPollution.rest.getStatus = HttpFetchStatus.Failed;
      state.cleaningConfiguration.environmentPollution.rest.getError = action.payload ?? null;
    });
    builder.addCase(patchCustomEnvironmentPollution.pending, (state, action) => {
      state.cleaningConfiguration.environmentPollution.rest.patchStatus = HttpFetchStatus.Loading;
      state.cleaningConfiguration.environmentPollution.rest.patchError = null;
    });
    builder.addCase(patchCustomEnvironmentPollution.fulfilled, (state, action) => {
      const customConfigIndex = state.cleaningConfiguration.environmentPollution.data?.findIndex(
        (ep) => ep.type === EnvironmentPollutionType.Custom,
      );
      if (customConfigIndex != null && customConfigIndex !== -1) {
        state.cleaningConfiguration.environmentPollution.data!.splice(customConfigIndex, 1);
        state.cleaningConfiguration.environmentPollution.data!.push(action.payload);
      }
      state.cleaningConfiguration.environmentPollution.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchCustomEnvironmentPollution.rejected, (state, action) => {
      state.cleaningConfiguration.environmentPollution.rest.patchStatus = HttpFetchStatus.Failed;
      state.cleaningConfiguration.environmentPollution.rest.patchError = action.payload ?? null;
    });

    builder.addCase(getNavLocalPlanner.pending, (state, action) => {
      state.nav.localPlanner.rest.getStatus = HttpFetchStatus.Loading;
      state.nav.localPlanner.rest.getError = null;
    });
    builder.addCase(getNavLocalPlanner.fulfilled, (state, action) => {
      state.nav.localPlanner.data = action.payload;
      state.nav.localPlanner.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getNavLocalPlanner.rejected, (state, action) => {
      state.nav.localPlanner.rest.getStatus = HttpFetchStatus.Failed;
      state.nav.localPlanner.rest.getError = action.payload ?? null;
    });
    builder.addCase(patchNavLocalPlanner.pending, (state, action) => {
      state.nav.localPlanner.rest.patchStatus = HttpFetchStatus.Loading;
      state.nav.localPlanner.rest.patchError = null;
    });
    builder.addCase(patchNavLocalPlanner.fulfilled, (state, action) => {
      state.nav.localPlanner.data = action.payload;
      state.nav.localPlanner.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchNavLocalPlanner.rejected, (state, action) => {
      state.nav.localPlanner.rest.patchStatus = HttpFetchStatus.Failed;
      state.nav.localPlanner.rest.patchError = action.payload ?? null;
    });
    builder.addCase(getNavTasks.pending, (state, action) => {
      state.nav.tasks.rest.getStatus = HttpFetchStatus.Loading;
      state.nav.tasks.rest.getError = null;
    });
    builder.addCase(getNavTasks.fulfilled, (state, action) => {
      state.nav.tasks.data = action.payload;
      state.nav.tasks.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getNavTasks.rejected, (state, action) => {
      state.nav.tasks.rest.getStatus = HttpFetchStatus.Failed;
      state.nav.tasks.rest.getError = action.payload ?? null;
    });
    builder.addCase(patchNavTasks.pending, (state, action) => {
      state.nav.tasks.rest.patchStatus = HttpFetchStatus.Loading;
      state.nav.tasks.rest.patchError = null;
    });
    builder.addCase(patchNavTasks.fulfilled, (state, action) => {
      state.nav.tasks.data = action.payload;
      state.nav.tasks.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchNavTasks.rejected, (state, action) => {
      state.nav.tasks.rest.patchStatus = HttpFetchStatus.Failed;
      state.nav.tasks.rest.patchError = action.payload ?? null;
    });

    builder.addCase(getExcludedDates.pending, (state, action) => {
      state.excludedDates.rest.getStatus = HttpFetchStatus.Loading;
      state.excludedDates.rest.getError = null;
    });
    builder.addCase(getExcludedDates.fulfilled, (state, action) => {
      state.excludedDates.data = action.payload;
      state.excludedDates.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getExcludedDates.rejected, (state, action) => {
      state.excludedDates.rest.getStatus = HttpFetchStatus.Failed;
      state.excludedDates.rest.getError = action.payload ?? null;
    });
    builder.addCase(postExcludedDate.pending, (state, action) => {
      state.excludedDates.rest.postStatus = HttpFetchStatus.Loading;
      state.excludedDates.rest.postError = null;
    });
    builder.addCase(postExcludedDate.fulfilled, (state, action) => {
      state.excludedDates.data = updateDataList(action.payload, state.excludedDates.data);
      state.excludedDates.rest.postStatus = HttpFetchStatus.Success;
    });
    builder.addCase(postExcludedDate.rejected, (state, action) => {
      state.excludedDates.rest.postStatus = HttpFetchStatus.Failed;
      state.excludedDates.rest.postError = action.payload ?? null;
    });
    builder.addCase(patchExcludedDateById.pending, (state, action) => {
      state.excludedDates.rest.patchStatus = HttpFetchStatus.Loading;
      state.excludedDates.rest.patchError = null;
    });
    builder.addCase(patchExcludedDateById.fulfilled, (state, action) => {
      state.excludedDates.data = updateDataList(action.payload, state.excludedDates.data);
      state.excludedDates.rest.patchStatus = HttpFetchStatus.Success;
    });
    builder.addCase(patchExcludedDateById.rejected, (state, action) => {
      state.excludedDates.rest.patchStatus = HttpFetchStatus.Failed;
      state.excludedDates.rest.patchError = action.payload ?? null;
    });
    builder.addCase(deleteExcludedDateById.pending, (state, action) => {
      state.excludedDates.rest.deleteStatus = HttpFetchStatus.Loading;
      state.excludedDates.rest.deleteError = null;
    });
    builder.addCase(deleteExcludedDateById.fulfilled, (state, action) => {
      const dateIndex = state.excludedDates.data?.findIndex((d) => d.id === action.payload);
      if (dateIndex != null && dateIndex !== -1) {
        state.excludedDates.data?.splice(dateIndex, 1);
      }
      state.excludedDates.rest.deleteStatus = HttpFetchStatus.Success;
    });
    builder.addCase(deleteExcludedDateById.rejected, (state, action) => {
      state.excludedDates.rest.deleteStatus = HttpFetchStatus.Failed;
      state.excludedDates.rest.deleteError = action.payload ?? null;
    });

    builder.addCase(getButtonConfiguration.pending, (state, action) => {
      state.buttonConfiguration.rest.getStatus = HttpFetchStatus.Loading;
      state.buttonConfiguration.rest.getError = null;
    });
    builder.addCase(getButtonConfiguration.fulfilled, (state, action) => {
      state.buttonConfiguration.data = action.payload;
      state.buttonConfiguration.rest.getStatus = HttpFetchStatus.Success;
    });
    builder.addCase(getButtonConfiguration.rejected, (state, action) => {
      state.buttonConfiguration.rest.getStatus = HttpFetchStatus.Failed;
      state.buttonConfiguration.rest.getError = action.payload ?? null;
    });
  },
});

export const {
  resetLocalRobotConfigurationState,
  updateCustomEnvironmentPollutionConfiguration,
  patchNavLocalPlannerRestSet,
  patchNavTasksRestSet,
  patchParamListRestSet,
  patchExcludedDatesRestSet,
  patchEnvironmentPollutionRestSet,
  toggleWaypointsEnabledAutomatically,
} = localRobotConfigurationSlice.actions;

export default localRobotConfigurationSlice.reducer;
